import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2, X } from "lucide-react";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const ENDPOINT = "https://api.mapbox.com/geocoding/v5/mapbox.places";

const cache = new Map(); // query -> {ts, results}
const CACHE_TTL = 5 * 60 * 1000; // 5 min

async function searchAddresses(query) {
  const q = query.trim().toLowerCase();
  if (!q || q.length < 2) return [];
  if (!MAPBOX_TOKEN) return [];
  const cached = cache.get(q);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.results;
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    autocomplete: "true",
    country: "it",
    language: "it",
    limit: "6",
    types: "address,poi,place,neighborhood,locality",
  });
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(q)}.json?${params.toString()}`);
  if (!res.ok) throw new Error("Geocoding failed");
  const data = await res.json();
  const results = (data.features || []).map((f) => ({
    id: f.id,
    primary: f.text || f.place_name?.split(",")[0] || "",
    secondary: f.place_name || "",
  }));
  cache.set(q, { ts: Date.now(), results });
  return results;
}

export default function AddressAutocomplete({
  id,
  value,
  onChange,
  placeholder = "Inserisci un indirizzo",
  required = false,
  className = "brut-input",
  testId,
}) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  // Sync external value -> local
  useEffect(() => {
    if (value !== undefined && value !== query) setQuery(value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const triggerSearch = useCallback((q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!q || q.trim().length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const r = await searchAddresses(q);
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    setOpen(true);
    setActiveIdx(-1);
    triggerSearch(v);
  };

  const pick = (item) => {
    setQuery(item.secondary);
    onChange(item.secondary);
    setOpen(false);
    setResults([]);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      if (activeIdx >= 0) {
        e.preventDefault();
        pick(results[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    // Delay so click on suggestion has time to fire
    blurTimerRef.current = setTimeout(() => setOpen(false), 180);
  };

  const handleFocus = () => {
    clearTimeout(blurTimerRef.current);
    if (results.length > 0) setOpen(true);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A91A8] pointer-events-none" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={`${className} pl-9 pr-9`}
          value={query}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          data-testid={testId}
        />
        {loading && (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2A6C] animate-spin" />
        )}
        {!loading && query && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setQuery(""); onChange(""); setResults([]); setOpen(false); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-full text-[#8A91A8] hover:bg-[#F5F6FA]"
            aria-label="Cancella"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <ul
          className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-2xl border border-[#ECEEF5] shadow-[0_10px_30px_rgba(20,32,80,0.12)] max-h-72 overflow-y-auto py-1"
          role="listbox"
          data-testid={testId ? `${testId}-suggestions` : "address-suggestions"}
        >
          {results.map((r, i) => (
            <li key={r.id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2.5 hover:bg-[#F5F6FA] ${activeIdx === i ? "bg-[#F5F6FA]" : ""}`}
                data-testid={`address-suggestion-${i}`}
                role="option"
                aria-selected={activeIdx === i}
              >
                <p className="text-sm font-semibold text-[#1A1D29] leading-tight">{r.primary}</p>
                <p className="text-xs text-[#8A91A8] mt-0.5 truncate">{r.secondary}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && query.trim().length >= 2 && results.length === 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white rounded-2xl border border-[#ECEEF5] shadow-[0_10px_30px_rgba(20,32,80,0.12)] px-3 py-3">
          <p className="text-sm text-[#8A91A8]">Nessun risultato. Premi invio per usare quello che hai scritto.</p>
        </div>
      )}
    </div>
  );
}
